import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchTeamRoles,
  grantTeamRoleRequest,
  revokeTeamRole,
  fetchTeams,
} from "../lib/api-client";
import type { TeamRoleGrant } from "../types/api";

function TeamSettingsPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();

  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [roleSubjectType, setRoleSubjectType] = useState<"user" | "service_account" | "email_domain">("user");
  const [roleSubjectId, setRoleSubjectId] = useState("");
  const [roleName, setRoleName] = useState("AnalyticsViewer");
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Fetch team details for the header/general settings
  const teamsQuery = useQuery({
    queryKey: ["teams"],
    queryFn: fetchTeams,
  });

  const currentTeam = teamsQuery.data?.teams.find((t) => t.id === teamId);

  const rolesQuery = useQuery({
    queryKey: ["team-roles", teamId],
    queryFn: () => fetchTeamRoles(teamId!),
    enabled: Boolean(teamId),
  });

  const grantRoleMutation = useMutation({
    mutationFn: async () => {
      if (!teamId) throw new Error("teamId missing");
      if (!roleSubjectId) throw new Error("Subject identifier is required");
      return grantTeamRoleRequest(teamId, {
        subjectType: roleSubjectType,
        subjectId: roleSubjectId,
        role: roleName,
      });
    },
    onSuccess() {
      setRoleSubjectId("");
      setShowGrantDialog(false);
      void queryClient.invalidateQueries({ queryKey: ["team-roles", teamId] });
      setToast({ type: "success", message: "Role granted successfully." });
      setTimeout(() => setToast(null), 3000);
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to grant role.",
      });
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      if (!teamId) throw new Error("teamId missing");
      await revokeTeamRole(teamId, roleId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["team-roles", teamId] });
      setToast({ type: "success", message: "Role revoked successfully." });
      setTimeout(() => setToast(null), 3000);
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to revoke role.",
      });
    },
  });

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--color-bg)" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600 }}>Team Settings</h1>
          <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)" }}>
            Configuration and advanced access control.
          </p>
        </div>
      </header>

      <main style={{ padding: "2rem", display: "grid", gap: "2rem", flex: 1, maxWidth: "1200px", margin: "0 auto", width: "100%" }}>

        {/* General Settings Section */}
        <section className="surface" style={{ padding: "1.5rem", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <header style={{ marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border)", paddingBottom: "1rem" }}>
            <h2 style={{ fontSize: "1.25rem", margin: 0, fontWeight: 600 }}>General Information</h2>
            <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
              Basic details about your team.
            </p>
          </header>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "1.5rem" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                Team Name
              </label>
              <div style={{ fontSize: "1rem", fontWeight: 500 }}>
                {currentTeam?.name || "Loading..."}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                Team Slug
              </label>
              <div style={{ fontSize: "1rem", fontFamily: "monospace", background: "var(--color-bg-soft)", display: "inline-block", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>
                {currentTeam?.slug || "—"}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                Plan Tier
              </label>
              <PlanBadge plan={currentTeam?.planTier || "free"} />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                Team ID
              </label>
              <div style={{ fontSize: "0.85rem", fontFamily: "monospace", color: "var(--color-text-muted)" }}>
                {teamId}
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Roles Section */}
        <section className="surface" style={{ padding: "1.5rem", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", margin: 0, fontWeight: 600 }}>Advanced Access</h2>
              <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                Manage access for Service Accounts and Email Domains.
              </p>
            </div>
            <button
              type="button"
              className="button secondary"
              onClick={() => setShowGrantDialog(true)}
            >
              Grant Access
            </button>
          </header>

          <RolesTable
            roles={rolesQuery.data?.roles ?? []}
            isLoading={rolesQuery.isLoading}
            onRevoke={(roleId) => revokeRoleMutation.mutate(roleId)}
            revoking={revokeRoleMutation.isPending}
          />
        </section>
      </main>

      {/* Grant Role Dialog */}
      {showGrantDialog && (
        <Dialog
          title="Grant Advanced Access"
          onClose={() => setShowGrantDialog(false)}
          onSubmit={() => grantRoleMutation.mutateAsync()}
          isLoading={grantRoleMutation.isPending}
          submitLabel="Grant Role"
        >
          <div className="form-group" style={{ display: "grid", gap: "1rem" }}>
            <label className="field">
              <span>Subject Type</span>
              <select
                value={roleSubjectType}
                onChange={(e) =>
                  setRoleSubjectType(e.target.value as "user" | "service_account" | "email_domain")
                }
                className="select"
              >
                <option value="user">User ID</option>
                <option value="service_account">Service Account</option>
                <option value="email_domain">Email Domain</option>
              </select>
            </label>

            <label className="field">
              <span>Identifier</span>
              <input
                type="text"
                value={roleSubjectId}
                onChange={(e) => setRoleSubjectId(e.target.value)}
                placeholder={roleSubjectType === "email_domain" ? "example.com" : "ID..."}
                required
                className="input"
              />
              <span className="help-text" style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                {roleSubjectType === "email_domain" ? "e.g. acme.com" : "Unique identifier for the subject."}
              </span>
            </label>

            <label className="field">
              <span>Role</span>
              <select
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                className="select"
              >
                <option value="AnalyticsViewer">Analytics Viewer</option>
                <option value="FormEditor">Form Editor</option>
                <option value="FormViewer">Form Viewer</option>
                <option value="FormPublisher">Form Publisher</option>
              </select>
            </label>
          </div>
        </Dialog>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: toast.type === "success" ? "#10B981" : "#EF4444",
            color: "white",
            padding: "0.8rem 1.5rem",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 2000,
            fontWeight: 500,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function RolesTable({
  roles,
  isLoading,
  onRevoke,
  revoking,
}: {
  roles: TeamRoleGrant[];
  isLoading: boolean;
  onRevoke: (roleId: string) => void;
  revoking: boolean;
}): JSX.Element {
  if (isLoading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>Loading grants...</div>;
  }
  if (roles.length === 0) {
    return (
      <div style={{ padding: "3rem 2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
        <div style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>No advanced roles granted</div>
        <p>Use advanced grants for service accounts or domain-wide access.</p>
      </div>
    );
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid var(--color-border)", textAlign: "left", color: "var(--color-text-muted)" }}>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Subject Type</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Identifier</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Role</th>
          <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Granted At</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {roles.map((role) => (
          <tr key={role.id} style={{ borderBottom: "1px solid var(--color-border-light)", transition: "background 0.2s" }}>
            <td style={{ padding: "0.75rem 1rem" }}>
              <span style={{
                background: "var(--color-bg-soft)",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "0.85rem",
                textTransform: "capitalize"
              }}>
                {role.subjectType.replace("_", " ")}
              </span>
            </td>
            <td style={{ padding: "0.75rem 1rem", fontFamily: "monospace", color: "var(--color-text)" }}>
              {role.subjectId}
            </td>
            <td style={{ padding: "0.75rem 1rem" }}>{role.role}</td>
            <td style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)" }}>{new Date(role.grantedAt).toLocaleDateString()}</td>
            <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>
              <button
                type="button"
                className="button ghost danger"
                onClick={() => {
                  if (confirm("Are you sure you want to revoke this role?")) {
                    onRevoke(role.id);
                  }
                }}
                disabled={revoking}
                style={{ fontSize: "0.85rem", padding: "0.3rem 0.6rem" }}
              >
                Revoke
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    free: { bg: "#F3F4F6", color: "#374151" }, // Gray
    pro: { bg: "#DBEAFE", color: "#1E40AF" }, // Blue
    enterprise: { bg: "#F3E8FF", color: "#6B21A8" }, // Purple
  };
  const style = styles[plan] || styles.free;
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: 600,
      textTransform: "capitalize"
    }}>
      {plan}
    </span>
  );
}

function Dialog({
  title,
  onClose,
  onSubmit,
  isLoading,
  children,
  submitLabel = "Save",
  width = "450px",
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  children: React.ReactNode;
  submitLabel?: string;
  width?: string;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "8px",
          padding: "0",
          width: width,
          maxWidth: "95vw",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          animation: "scaleIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--color-border)" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>{title}</h2>
        </div>

        <div style={{ padding: "1.5rem", overflowY: "auto" }}>
          <form
            id="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            {children}
          </form>
        </div>

        <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: "0.75rem", background: "var(--color-bg-soft)", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
          <button type="button" className="button ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" form="dialog-form" className="button primary" disabled={isLoading}>
            {isLoading ? "Processing..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamSettingsPage;
